import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ContentCopy as CopyIcon,
  CheckCircle as SuccessIcon,
  CheckCircle,
  Error as ErrorIcon,
  AccessTime as TimeIcon,
  Memory as TokenIcon,
  Speed as SpeedIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Calculate as CalculateIcon,
  SwapHoriz as SwapIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { JsonViewer } from '../components/JsonViewer';
import { PlatformUsagePreviewDialog } from '../components/PlatformUsagePreviewDialog';
import { RunFeedbackButtons } from '../components/RunFeedbackButtons';
import { RunFeedbackList } from '../components/RunFeedbackList';
import { KnowledgeTraceView } from '../components/learning/common';
import { ReliabilityTraceView, modelChangeExplanation } from '../components/reliability/ReliabilityTraceView';
import { isAdminUser } from '../utils/auth';
import { QuickLinks } from '../components/performance/QuickLinks';
import { PlatformRequestId } from '../components/PlatformRequestId';
import { PageBreadcrumbs } from '../components/PageBreadcrumbs';
import { CopyId } from '../components/CopyId';
import { RelativeTime } from '../components/RelativeTime';
import { usePageTitle } from '../utils/pageTitle';

interface RunDetails {
  id: string;
  function_id: string;
  function_name: string;
  function_version?: number;
  function_is_public?: boolean;
  customer_id?: string;
  created_at: string;
  execution_time_ms: number;
  success: boolean;
  model_used: string;
  requested_model?: string | null;
  selected_model?: string | null;
  model_changed?: boolean;
  model_change_reason?: 'zdr_substitute' | 'reliability_fallback' | null;
  model_default?: boolean;
  model_spread?: string[] | null;
  input_type: string;
  input_data?: any;
  output_data?: any;
  error?: string;
  token_usage?: { 
    prompt_tokens?: number; 
    completion_tokens?: number; 
    total_tokens?: number;
    input_tokens?: number;
    output_tokens?: number;
    cost_estimate?: number;
  };
  total_tokens?: number;
  tokens?: number;
  ai_credits?: number;
  multiplier?: number;
  ip_address?: string;
  user_agent?: string;
  content_redacted?: boolean;
  redaction_reason?: 'zdr_request' | 'no_store' | 'retention' | null;
  metadata?: any;
}

const redactionMessage = (reason?: string | null): string => {
  switch (reason) {
    case 'zdr_request':
      return 'Not stored: the caller required zero data retention for this call. Only usage (model, tokens, timing) is kept for billing.';
    case 'no_store':
      return "Not stored: this account's data retention is set to \"Do not store\". Only usage (model, tokens, timing) is kept for billing.";
    default:
      return "Removed: this run is older than the account's data retention period. Only usage (model, tokens, timing) is kept for billing.";
  }
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface AuthenticatedImageProps {
  fileType: 'input' | 'output';
  runId: string;
  alt: string;
  style?: React.CSSProperties;
  onError?: () => void;
}

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({ fileType, runId, alt, style, onError }) => {
  const { user } = useAuth();
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadImage = async () => {
      if (!user) return;
      
      try {
        const idToken = await user.getIdToken();
        const response = await fetch(`/api/v2/analytics/runs/${runId}/file/${fileType}`, {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to load image');
        }
        
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        
        if (mounted) {
          setImageSrc(url);
          setLoading(false);
        }
      } catch (err) {

        if (mounted) {
          setError(true);
          setLoading(false);
          onError?.();
        }
      }
    };

    loadImage();

    return () => {
      mounted = false;
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileType, runId, user, onError]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, ...style }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error || !imageSrc) {
    return null;
  }

  return <img src={imageSrc} alt={alt} style={style} />;
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`run-tabpanel-${index}`}
      aria-labelledby={`run-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const IndividualRunDetails: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions } = useAuth();
  const stateRun: RunDetails | undefined = location.state?.run;
  const [loading, setLoading] = useState(!stateRun);
  const [runDetails, setRunDetails] = useState<RunDetails | null>(stateRun || null);
  const [fullLoaded, setFullLoaded] = useState(false);
  const [feedbackVersion, setFeedbackVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [platformPreviewOpen, setPlatformPreviewOpen] = useState(false);
  const isPlatformAdmin = isAdminUser(permissions?.userType);
  usePageTitle(runDetails?.function_name ? `${runDetails.function_name} · Run` : 'Run details');

  useEffect(() => {
    if (!runId || !user) return;
    const controller = new AbortController();
    (async () => {
      try {
        const idToken = await user.getIdToken();
        const res = await fetch(`/api/v2/analytics/runs/${runId}`, {
          headers: { Authorization: `Bearer ${idToken}` },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(res.status === 404 ? 'Run not found' : 'Failed to load run details');
        const body = await res.json();
        setRunDetails(body.data);
        setFullLoaded(true);
        setError(null);
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load run details');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [runId, user]);

  const handleCopy = async (data: any, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(
        typeof data === 'string' ? data : JSON.stringify(data, null, 2)
      );
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {

    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const estimateSize = (data: any) => {
    try {
      return new Blob([JSON.stringify(data)]).size;
    } catch {
      return 0;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!runDetails) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Alert severity="error">{error || 'Run details not found'}</Alert>
      </Box>
    );
  }

  const runOwnerIsOther = Boolean(runDetails.customer_id && runDetails.customer_id !== permissions?.customerId);
  const functionViewParams = runOwnerIsOther && runDetails.customer_id ? new URLSearchParams({ customer_id: runDetails.customer_id }) : undefined;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate(-1)}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <PageBreadcrumbs
            items={[
              {
                label: runOwnerIsOther ? `Runs & Performance · ${runDetails.customer_id}` : 'Runs & Performance',
                to: `/dashboard/function-runs${functionViewParams ? `?${functionViewParams}` : ''}`,
              },
              ...(runDetails.function_id
                ? [{
                    label: runDetails.function_name,
                    to: `/dashboard/function-runs/${runDetails.function_id}${functionViewParams ? `?${functionViewParams}` : ''}`,
                  }]
                : []),
              { label: 'Run' },
            ]}
          />
          <Typography variant="h4">Run Details</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', color: 'text.secondary' }}>
            {runDetails.id && <CopyId label="Run" value={runDetails.id} short />}
            {runDetails.created_at && <RelativeTime value={runDetails.created_at} />}
          </Box>
        </Box>
        {runDetails.id && (
          <RunFeedbackButtons sourceType="function_run" sourceId={runDetails.id} onChange={() => setFeedbackVersion((v) => v + 1)} />
        )}
        {runDetails.function_id && !runDetails.function_is_public && (
          <QuickLinks
            kind="function"
            id={runDetails.function_id}
            name={runDetails.function_name}
            editable={!runOwnerIsOther}
            showViews
            viewParams={functionViewParams}
          />
        )}
        {isPlatformAdmin && runDetails.id && (
          <Button
            variant="outlined"
            startIcon={<CalculateIcon />}
            onClick={() => setPlatformPreviewOpen(true)}
          >
            Convert to Platform Token Usage
          </Button>
        )}
        <Chip
          icon={runDetails.success ? <SuccessIcon /> : <ErrorIcon />}
          label={runDetails.success ? 'Success' : 'Failed'}
          color={runDetails.success ? 'success' : 'error'}
        />
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Function
              </Typography>
              <Typography variant="h6" noWrap>
                {runDetails.function_id ? (
                  <Link
                    component={RouterLink}
                    to={`/dashboard/function-runs/${runDetails.function_id}${functionViewParams ? `?${functionViewParams}` : ''}`}
                    underline="hover"
                    color="inherit"
                  >
                    {runDetails.function_name}
                  </Link>
                ) : (
                  runDetails.function_name
                )}
              </Typography>
              {runDetails.function_version && (
                <Typography variant="body2" color="textSecondary">
                  Version {runDetails.function_version}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimeIcon color="action" fontSize="small" />
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Execution Time
                </Typography>
              </Box>
              <Typography variant="h6">
                {formatDuration(runDetails.execution_time_ms)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formatDate(runDetails.created_at)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SpeedIcon color="action" fontSize="small" />
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Model
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>
                {runDetails.model_used || '-'}
              </Typography>
              {runDetails.model_changed && runDetails.requested_model && runDetails.requested_model !== runDetails.model_used ? (
                <Typography variant="body2" color="warning.main">
                  {runDetails.model_spread?.length ? 'Started on' : 'Requested'} {runDetails.requested_model}
                </Typography>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {runDetails.model_spread?.length
                    ? `Spread across ${runDetails.model_spread.length} models`
                    : runDetails.model_default
                      ? "Function's default model"
                      : 'Model requested by the caller'}
                </Typography>
              )}
              <Typography variant="body2" color="textSecondary">
                {runDetails.input_type} input
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TokenIcon color="action" fontSize="small" />
                <Typography color="textSecondary" variant="body2">
                  Tokens
                </Typography>
              </Box>
              <Typography variant="h6">
                {(() => {
                  // Get tokens from new simplified field with fallbacks
                  const tokens = runDetails.tokens ?? 
                                runDetails.total_tokens ?? 
                                runDetails.token_usage?.total_tokens ?? 
                                runDetails.metadata?.llm_response?.usage?.total_tokens;
                  return tokens ? tokens.toLocaleString() : '-';
                })()}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography color="textSecondary" variant="body2">
                  AI Credits
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ color: '#667eea' }}>
                {(() => {
                  const credits = runDetails.ai_credits;
                  return credits ? credits.toFixed(2) : '-';
                })()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <PlatformRequestId
        requestId={runDetails.metadata?.passio_request_id}
        customerId={runOwnerIsOther ? runDetails.customer_id : null}
        sx={{ mb: 3 }}
      />

      {/* Error Alert */}
      {!runDetails.success && runDetails.error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Error Message
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
            {runDetails.error}
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}. Showing the summary from the list; input, output and metadata are unavailable.
        </Alert>
      )}

      {runDetails.model_changed && runDetails.model_change_reason && (
        <Alert severity="info" icon={<SwapIcon />} sx={{ mb: 3 }}>
          <Typography variant="subtitle2">
            Requested {runDetails.requested_model}, ran on {runDetails.model_used}
          </Typography>
          <Typography variant="body2">{modelChangeExplanation(runDetails)}</Typography>
        </Alert>
      )}

      {(runDetails.metadata?.llm_response?.reliability || runDetails.metadata?.reliability) && (
        <Box sx={{ mb: 3 }}>
          <ReliabilityTraceView trace={runDetails.metadata.llm_response?.reliability || runDetails.metadata.reliability} />
        </Box>
      )}

      {runDetails.metadata?.knowledge && (
        <Box sx={{ mb: 3 }}>
          <KnowledgeTraceView trace={runDetails.metadata.knowledge} title="Knowledge this run received" />
        </Box>
      )}

      {runDetails.id && (
        <RunFeedbackList
          sourceType="function_run"
          sourceId={runDetails.id}
          customerId={runOwnerIsOther ? runDetails.customer_id : null}
          refreshKey={feedbackVersion}
        />
      )}

      {!fullLoaded && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Main Content Tabs */}
      {fullLoaded && (
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label="Input" />
            <Tab label="Output" />
            <Tab label="System Info" />
            {runDetails.metadata && <Tab label="Metadata" />}
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 2 }}>
            {runDetails.content_redacted ? (
              <Alert severity="info">{redactionMessage(runDetails.redaction_reason)}</Alert>
            ) : runDetails.input_data ? (
              (() => {
                // Check if this is a chain step with upstream context
                const isChainStep = runDetails.metadata?.is_chain_step || 
                                   (runDetails.input_data?.input && 
                                    (runDetails.input_data.input.__upstream || runDetails.input_data.input.__chain_input));
                
                // Extract effective input vs context for chain steps
                let effectiveInput: any = runDetails.input_data;
                let upstreamContext: any = null;
                let chainInput: any = null;
                let sectionNumber: number | null = null;
                let totalSections: number | null = null;
                
                if (isChainStep && runDetails.input_data?.input) {
                  const { __upstream, __chain_input, ...actualInput } = runDetails.input_data.input;
                  effectiveInput = { input: actualInput, metadata: runDetails.input_data.metadata };
                  upstreamContext = __upstream;
                  chainInput = __chain_input;
                  
                  // Extract section info for video clip runs
                  sectionNumber = actualInput.section_number;
                  // Try to get total sections from upstream
                  if (__upstream) {
                    const upstreamKeys = Object.keys(__upstream);
                    for (const key of upstreamKeys) {
                      const upstreamData = __upstream[key];
                      if (upstreamData?.sections?.length) {
                        totalSections = upstreamData.sections.length;
                        break;
                      }
                      if (upstreamData?.section_count) {
                        totalSections = upstreamData.section_count;
                        break;
                      }
                    }
                  }
                }
                
                return (
                  <Box>
                    {/* Section indicator for video clip generation */}
                    {isChainStep && sectionNumber != null && (
                      <Alert 
                        severity="info" 
                        sx={{ mb: 2 }}
                        icon={<span style={{ fontSize: '1.1rem' }}>🎬</span>}
                      >
                        <Typography variant="body2">
                          <strong>Video Clip Section {sectionNumber}</strong>
                          {totalSections && ` of ${totalSections}`}
                          {' — '}This run generated one video clip. The upstream context below shows all sections for reference.
                        </Typography>
                      </Alert>
                    )}
                    
                    {/* Main Input Section */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box>
                        <Typography variant="h6">
                          {isChainStep ? 'Effective Input' : 'Input Data'}
                          {sectionNumber != null && (
                            <Chip 
                              label={`Section ${sectionNumber}${totalSections ? ` / ${totalSections}` : ''}`}
                              size="small" 
                              color="primary"
                              sx={{ ml: 1, fontWeight: 'bold' }} 
                            />
                          )}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Type: {runDetails.input_type} • Size: {formatBytes(estimateSize(effectiveInput))}
                          {isChainStep && !sectionNumber && (
                            <Chip 
                              label="Chain Step" 
                              size="small" 
                              color="info" 
                              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
                            />
                          )}
                        </Typography>
                      </Box>
                      <Button
                        startIcon={copiedField === 'input' ? <CheckCircle /> : <CopyIcon />}
                        onClick={() => handleCopy(effectiveInput, 'input')}
                        color={copiedField === 'input' ? 'success' : 'primary'}
                      >
                        {copiedField === 'input' ? 'Copied!' : 'Copy'}
                      </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    {/* Render effective input */}
                    {(() => {
                // Check if input contains a GCS file reference
                if (typeof effectiveInput === 'object' && 
                    effectiveInput.input &&
                    typeof effectiveInput.input === 'object' &&
                    effectiveInput.input.type === 'gcs_file') {
                  const gcsFile = effectiveInput.input;
                  return (
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Uploaded File
                      </Typography>
                      <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="textSecondary">File Type</Typography>
                            <Typography variant="body1">{gcsFile.mime_type || 'Unknown'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="textSecondary">File Size</Typography>
                            <Typography variant="body1">{formatBytes(gcsFile.size || 0)}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="textSecondary">Storage Path</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {gcsFile.path || gcsFile.url}
                            </Typography>
                          </Grid>
                          {/* Display file preview or download link */}
                          {gcsFile.mime_type && (
                            <Grid item xs={12}>
                              <Box sx={{ mt: 2 }}>
                                {(() => {
                                  // Construct the public URL for the GCS file
                                  const bucket = gcsFile.bucket || (gcsFile.url && gcsFile.url.match(/gcs:\/\/([^/]+)/)?.[1]);
                                  const path = gcsFile.path || (gcsFile.url && gcsFile.url.replace(/^gcs:\/\/[^/]+\//, ''));
                                  
                                  if (bucket && path) {
                                    const proxyUrl = `/api/v2/analytics/runs/${runDetails.id}/file/input`;
                                    const isImage = gcsFile.mime_type.startsWith('image/');
                                    
                                    return (
                                      <Box>
                                        {isImage ? (
                                          <>
                                            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                                              Preview
                                            </Typography>
                                            <Box sx={{ 
                                              position: 'relative',
                                              width: '100%',
                                              maxWidth: '600px',
                                              margin: '0 auto',
                                              border: '1px solid',
                                              borderColor: 'divider',
                                              borderRadius: 1,
                                              overflow: 'hidden',
                                              backgroundColor: 'background.paper'
                                            }}>
                                              <AuthenticatedImage
                                                fileType="input"
                                                runId={runDetails.id}
                                                alt="Uploaded file preview"
                                                style={{ 
                                                  width: '100%', 
                                                  height: 'auto',
                                                  display: 'block'
                                                }}
                                                onError={() => {
                                                  // Show error message
                                                  const errorDiv = document.querySelector('.image-error-message') as HTMLElement;
                                                  if (errorDiv) errorDiv.style.display = 'block';
                                                }}
                                              />
                                              <Box className="image-error-message" sx={{ 
                                                display: 'none', 
                                                p: 3, 
                                                textAlign: 'center',
                                                color: 'text.secondary'
                                              }}>
                                                <Typography variant="body2">
                                                  Unable to load image preview
                                                </Typography>
                                                <Typography variant="caption">
                                                  Error loading image from server
                                                </Typography>
                                              </Box>
                                            </Box>
                                          </>
                                        ) : (
                                          <Box sx={{ 
                                            p: 3, 
                                            textAlign: 'center',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            backgroundColor: 'action.hover'
                                          }}>
                                            <FileIcon sx={{ fontSize: 48, color: 'action.active', mb: 1 }} />
                                            <Typography variant="body2" color="textSecondary">
                                              {gcsFile.mime_type}
                                            </Typography>
                                          </Box>
                                        )}
                                        <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<OpenInNewIcon />}
                                            href={proxyUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            {isImage ? 'Open Full Size' : 'View File'}
                                          </Button>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<DownloadIcon />}
                                            href={proxyUrl}
                                            download
                                          >
                                            Download
                                          </Button>
                                        </Box>
                                      </Box>
                                    );
                                  }
                                  return (
                                    <Typography variant="body2" color="textSecondary">
                                      Unable to construct file URL
                                    </Typography>
                                  );
                                })()}
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                      {/* Show other fields if present (text instructions, metadata, etc) */}
                      {(() => {
                        const {input, ...otherFields} = effectiveInput;
                        // Check if we have meaningful other fields
                        const hasOtherData = Object.keys(otherFields).some(key => 
                          otherFields[key] !== null && otherFields[key] !== undefined
                        );
                        
                        if (!hasOtherData) return null;
                        
                        return (
                          <Box sx={{ mt: 3 }}>
                            {otherFields.message && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                                  User Instructions
                                </Typography>
                                <Paper sx={{ p: 2 }}>
                                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {otherFields.message}
                                  </Typography>
                                </Paper>
                              </Box>
                            )}
                            {Object.keys(otherFields).filter(k => k !== 'message' && otherFields[k] != null).length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Additional Input Data:</Typography>
                                <JsonViewer data={(() => {
                                  const {message, ...rest} = otherFields;
                                  return rest;
                                })()} />
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                    </Box>
                  );
                }
                
                // Check if input is a base64 image string (direct string or in an object)
                let imageData: string | null = null;
                if (typeof effectiveInput === 'string' && 
                    effectiveInput.startsWith('data:image/')) {
                  imageData = effectiveInput;
                } else if (typeof effectiveInput === 'object' && 
                           effectiveInput.input &&
                           typeof effectiveInput.input === 'string' &&
                           effectiveInput.input.startsWith('data:image/')) {
                  imageData = effectiveInput.input;
                }
                
                if (imageData) {
                  const base64Length = imageData.length;
                  const imageType = imageData.match(/data:image\/(\w+);/)?.[1] || 'unknown';
                  return (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Image Data ({imageType.toUpperCase()}) - {formatBytes(base64Length)}
                      </Typography>
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: 'action.hover', 
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {imageData.substring(0, 100)}...
                      </Box>
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                        Full base64 string truncated for display
                      </Typography>
                      {/* If input_data is an object with more fields, show them */}
                      {typeof effectiveInput === 'object' && Object.keys(effectiveInput).length > 1 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>Other Input Fields:</Typography>
                          <JsonViewer data={(() => {
                            const {input, ...otherFields} = effectiveInput;
                            return otherFields;
                          })()} />
                        </Box>
                      )}
                    </Box>
                  );
                }
                // Check if it's simple text input
                if (typeof effectiveInput === 'string') {
                  return (
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        {runDetails.input_type === 'text' ? 'Text Input' : 'Input Data'}
                      </Typography>
                      <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {effectiveInput}
                        </Typography>
                      </Paper>
                    </Box>
                  );
                }
                
                // Check if input has both text and other components
                if (typeof effectiveInput === 'object' && 
                    effectiveInput.text && 
                    Object.keys(effectiveInput).length > 1) {
                  return (
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Multi-Modal Input
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                          Text Component
                        </Typography>
                        <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {effectiveInput.text}
                          </Typography>
                        </Paper>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Additional Components:</Typography>
                        <JsonViewer data={(() => {
                          const {text, ...rest} = effectiveInput;
                          return rest;
                        })()} />
                      </Box>
                    </Box>
                  );
                }
                
                // For other data types, use JsonViewer
                return <JsonViewer data={effectiveInput} />;
              })()}
                    
                    {/* Chain Context Section - collapsible */}
                    {isChainStep && (chainInput || upstreamContext) && (
                      <Box sx={{ mt: 4 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          🔗 Chain Context
                          <Typography variant="caption" color="textSecondary">
                            (Data from previous nodes)
                          </Typography>
                        </Typography>
                        
                        {/* Original Chain Input */}
                        {chainInput && (
                          <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle2" color="textSecondary">
                                📥 Original Chain Input
                              </Typography>
                              <Button
                                size="small"
                                startIcon={copiedField === 'chainInput' ? <CheckCircle /> : <CopyIcon />}
                                onClick={() => handleCopy(chainInput, 'chainInput')}
                                color={copiedField === 'chainInput' ? 'success' : 'primary'}
                              >
                                {copiedField === 'chainInput' ? 'Copied!' : 'Copy'}
                              </Button>
                            </Box>
                            <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                              <JsonViewer data={chainInput} />
                            </Paper>
                          </Box>
                        )}
                        
                        {/* Upstream Node Data */}
                        {upstreamContext && (
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle2" color="textSecondary">
                                ⬆️ Upstream Node Outputs
                                <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                  (Full context from previous nodes - typically contains all items, not just this run's item)
                                </Typography>
                              </Typography>
                              <Button
                                size="small"
                                startIcon={copiedField === 'upstream' ? <CheckCircle /> : <CopyIcon />}
                                onClick={() => handleCopy(upstreamContext, 'upstream')}
                                color={copiedField === 'upstream' ? 'success' : 'primary'}
                              >
                                {copiedField === 'upstream' ? 'Copied!' : 'Copy'}
                              </Button>
                            </Box>
                            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <JsonViewer data={upstreamContext} />
                            </Paper>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })()
            ) : (
              <Typography color="textSecondary">No input data available</Typography>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">Output Data</Typography>
                <Typography variant="body2" color="textSecondary">
                  Size: {formatBytes(estimateSize(runDetails.output_data))}
                </Typography>
              </Box>
              <Button
                startIcon={copiedField === 'output' ? <CheckCircle /> : <CopyIcon />}
                onClick={() => handleCopy(runDetails.output_data, 'output')}
                color={copiedField === 'output' ? 'success' : 'primary'}
                disabled={!runDetails.output_data}
              >
                {copiedField === 'output' ? 'Copied!' : 'Copy'}
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {runDetails.content_redacted ? (
              <Alert severity="info">{redactionMessage(runDetails.redaction_reason)}</Alert>
            ) : runDetails.output_data ? (
              (() => {
                // Check if this is an image generation response
                if (runDetails.output_data && 
                    typeof runDetails.output_data === 'object' && 
                    'images' in runDetails.output_data) {
                  const images = runDetails.output_data.images;
                  if (Array.isArray(images) && images.length > 0) {
                    return (
                      <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                        <Typography variant="h6" gutterBottom>Generated Images:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          {images.map((img: any, idx: number) => (
                            <Box key={idx} sx={{ maxWidth: '100%' }}>
                              {img.url && (
                                <Box>
                                  <img 
                                    src={img.url} 
                                    alt={`Generated output ${idx + 1}`}
                                    style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                                  />
                                  {img.revised_prompt && (
                                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                      Revised prompt: {img.revised_prompt}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {img.data && (
                                <Box>
                                  <img 
                                    src={img.data} 
                                    alt={`Generated output ${idx + 1}`}
                                    style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                                  />
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Paper>
                    );
                  }
                }
                
                // Check if output contains a GCS file reference
                if (typeof runDetails.output_data === 'object' && 
                    runDetails.output_data !== null &&
                    runDetails.output_data.type === 'gcs_file') {
                  const gcsFile = runDetails.output_data;
                  const bucket = gcsFile.bucket || (gcsFile.url && gcsFile.url.match(/gcs:\/\/([^/]+)/)?.[1]);
                  const path = gcsFile.path || (gcsFile.url && gcsFile.url.replace(/^gcs:\/\/[^/]+\//, ''));
                  
                  if (bucket && path) {
                    const proxyUrl = `/api/v2/analytics/runs/${runDetails.id}/file/output`;
                    const isImage = gcsFile.mime_type && gcsFile.mime_type.startsWith('image/');
                    
                    return (
                      <Box>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          Generated File
                        </Typography>
                        <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="subtitle2" color="textSecondary">File Type</Typography>
                              <Typography variant="body1">{gcsFile.mime_type || 'Unknown'}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="subtitle2" color="textSecondary">File Size</Typography>
                              <Typography variant="body1">{formatBytes(gcsFile.size || 0)}</Typography>
                            </Grid>
                          </Grid>
                          {isImage && (
                            <Box sx={{ mt: 2 }}>
                              <Box sx={{ 
                                width: '100%', 
                                maxWidth: '400px',
                                margin: '0 auto',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                overflow: 'hidden'
                              }}>
                                <AuthenticatedImage
                                  fileType="output"
                                  runId={runDetails.id}
                                  alt="Generated output"
                                  style={{ 
                                    width: '100%', 
                                    height: 'auto',
                                    display: 'block'
                                  }}
                                />
                              </Box>
                            </Box>
                          )}
                          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<OpenInNewIcon />}
                              href={proxyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View File
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<DownloadIcon />}
                              href={proxyUrl}
                              download
                            >
                              Download
                            </Button>
                          </Box>
                        </Paper>
                      </Box>
                    );
                  }
                }
                
                // Check if output is a simple string response
                if (typeof runDetails.output_data === 'string') {
                  return (
                    <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {runDetails.output_data}
                      </Typography>
                    </Paper>
                  );
                }
                
                // Check if this is a text response wrapped in the Response object
                if (runDetails.output_data && 
                    typeof runDetails.output_data === 'object' && 
                    'response' in runDetails.output_data && 
                    Object.keys(runDetails.output_data).length === 1) {
                  const responseText = runDetails.output_data.response;
                  
                  return (
                    <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {responseText}
                      </Typography>
                    </Paper>
                  );
                }
                
                // For arrays and objects, use JsonViewer
                return <JsonViewer data={runDetails.output_data} />;
              })()
            ) : (
              <Typography color="textSecondary">
                {runDetails.success ? 'No output data available' : 'Failed to generate output'}
              </Typography>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>System Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Run ID</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
                  {runDetails.id}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Function ID</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
                  {runDetails.function_id}
                </Typography>
              </Grid>
              {runDetails.ip_address && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">IP Address</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {runDetails.ip_address}
                  </Typography>
                </Grid>
              )}
              {runDetails.user_agent && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">User Agent</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {runDetails.user_agent}
                  </Typography>
                </Grid>
              )}
              {runDetails.token_usage?.cost_estimate && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Estimated Cost</Typography>
                  <Typography variant="body1">
                    ${runDetails.token_usage.cost_estimate.toFixed(4)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </TabPanel>

        {runDetails.metadata && (
          <TabPanel value={tabValue} index={3}>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Metadata</Typography>
                <Button
                  startIcon={copiedField === 'metadata' ? <CheckCircle /> : <CopyIcon />}
                  onClick={() => handleCopy(runDetails.metadata, 'metadata')}
                  color={copiedField === 'metadata' ? 'success' : 'primary'}
                >
                  {copiedField === 'metadata' ? 'Copied!' : 'Copy'}
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <JsonViewer data={runDetails.metadata} />
            </Box>
          </TabPanel>
        )}
      </Paper>
      )}
      <PlatformUsagePreviewDialog
        open={platformPreviewOpen}
        onClose={() => setPlatformPreviewOpen(false)}
        title="Platform Usage Preview"
        endpoint={runDetails.id ? `/api/v2/analytics/admin/runs/${runDetails.id}/platform-usage-preview` : null}
      />
    </Box>
  );
};
